import { MapPin, Calendar, Truck, Package, Phone, Mail, RefreshCw, Flame, Clock, MessageCircle } from 'lucide-react';

const openChatWithLoad = (ref) => {
  window.dispatchEvent(new CustomEvent('rbx:open-chat-with-load', { detail: { ref } }));
};

const formatDate = (dateStr) => {
  if (!dateStr) return 'TBD';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return 'TBD';
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

const timeAgo = (isoStr) => {
  if (!isoStr) return null;
  const d = new Date(isoStr);
  if (isNaN(d.getTime())) return null;
  const secs = Math.floor((Date.now() - d.getTime()) / 1000);
  if (secs < 60) return 'just now';
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
};

export const LoadsTable = ({ loads, loading, onRefresh, onClear, meta }) => (
  <section className="py-8">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      {meta?.active_count > 0 && (
        <div className="flex items-center gap-3 mb-4 text-sm" data-testid="loads-meta">
          <span className="flex items-center gap-1.5 text-red-600 font-bold">
            <Flame className="w-4 h-4" />
            {meta.active_count} hot load{meta.active_count !== 1 ? 's' : ''}
          </span>
          {meta.last_updated && (
            <span className="flex items-center gap-1 text-zinc-400">
              <Clock className="w-3.5 h-3.5" />
              Updated {timeAgo(meta.last_updated)}
            </span>
          )}
        </div>
      )}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-xl font-bold text-zinc-900">Available Loads</h2>
          <p className="text-sm text-zinc-500">{loads.length} loads found</p>
        </div>
        <button
          onClick={onRefresh}
          className="flex items-center gap-2 text-zinc-600 hover:text-zinc-900 transition-colors h-10 px-3 -mr-3"
          data-testid="refresh-loads-btn"
        >
          <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          <span className="text-sm font-medium">Refresh</span>
        </button>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-20">
          <RefreshCw className="w-8 h-8 animate-spin text-zinc-400" />
        </div>
      )}

      {!loading && loads.length === 0 && (
        <div className="text-center py-20 bg-white border border-zinc-200">
          <Package className="w-16 h-16 text-zinc-300 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-zinc-700 mb-2">No loads found</h3>
          <p className="text-zinc-500 mb-6">Try adjusting your filters or check back later.</p>
          <button onClick={onClear} className="text-red-600 font-semibold hover:underline">
            Clear all filters
          </button>
        </div>
      )}

      {!loading && loads.length > 0 && (
        <>
          {/* Mobile / sm — stacked card list */}
          <div className="md:hidden space-y-3" data-testid="loads-card-list">
            {loads.map((load) => (
              <article
                key={load.id}
                className="bg-white border border-zinc-200 p-4 space-y-3"
                data-testid={`load-card-${load.reference_no}`}
              >
                <header className="flex items-center justify-between gap-2">
                  <span className="font-mono font-bold text-zinc-900 text-sm">#{load.reference_no}</span>
                  <span className="inline-flex items-center px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide bg-zinc-100 text-zinc-700">
                    {load.load_type}
                  </span>
                </header>

                <div className="space-y-1.5">
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-green-600 flex-shrink-0" />
                    <span className="text-sm font-medium text-zinc-900 truncate">{load.pickup}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-red-600 flex-shrink-0" />
                    <span className="text-sm font-medium text-zinc-900 truncate">{load.destination}</span>
                  </div>
                </div>

                <dl className="grid grid-cols-2 gap-2 text-xs border-t border-zinc-100 pt-3">
                  <div>
                    <dt className="text-[10px] uppercase tracking-wider text-zinc-400 mb-0.5">Pickup</dt>
                    <dd className="text-zinc-700 font-medium">{formatDate(load.pickup_date)}</dd>
                  </div>
                  <div>
                    <dt className="text-[10px] uppercase tracking-wider text-zinc-400 mb-0.5">Equipment</dt>
                    <dd className="text-zinc-700 font-medium">{load.truck_type}</dd>
                  </div>
                  <div>
                    <dt className="text-[10px] uppercase tracking-wider text-zinc-400 mb-0.5">Weight</dt>
                    <dd className="text-zinc-700 font-mono">{load.weight.toLocaleString()} lbs</dd>
                  </div>
                  <div>
                    <dt className="text-[10px] uppercase tracking-wider text-zinc-400 mb-0.5">Miles</dt>
                    <dd className="text-zinc-700 font-mono">{load.miles ? `${load.miles} mi` : '—'}</dd>
                  </div>
                  {load.commodity && (
                    <div className="col-span-2">
                      <dt className="text-[10px] uppercase tracking-wider text-zinc-400 mb-0.5">Commodity</dt>
                      <dd className="text-zinc-700">{load.commodity}</dd>
                    </div>
                  )}
                </dl>

                <div className="pt-2 border-t border-zinc-100">
                  <div className="flex flex-col gap-1 mb-2 text-xs">
                    <div className="flex items-center gap-1.5 text-zinc-600">
                      <Phone className="w-3 h-3 flex-shrink-0 text-red-600" />
                      <span className="font-medium">{load.agent_phone}</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-zinc-500 break-all">
                      <Mail className="w-3 h-3 flex-shrink-0" />
                      <span>{load.agent_email}</span>
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <a
                      href={`tel:${load.agent_phone}`}
                      className="flex-1 flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white px-3 py-2.5 text-sm font-bold uppercase tracking-wider"
                      data-testid={`call-agent-${load.reference_no}`}
                    >
                      <Phone className="w-3.5 h-3.5" />
                      Call
                    </a>
                    <a
                      href={`mailto:${load.agent_email}`}
                      className="flex-1 flex items-center justify-center gap-2 border border-zinc-300 hover:border-zinc-900 text-zinc-700 px-3 py-2.5 text-sm font-bold uppercase tracking-wider"
                      data-testid={`email-agent-${load.reference_no}`}
                    >
                      <Mail className="w-3.5 h-3.5" />
                      Email
                    </a>
                  </div>
                  <button
                    onClick={() => openChatWithLoad(load.reference_no)}
                    className="w-full mt-2 flex items-center justify-center gap-2 bg-zinc-900 hover:bg-red-600 text-white px-3 py-2.5 text-sm font-bold uppercase tracking-wider transition-colors"
                    data-testid={`chat-about-load-${load.reference_no}`}
                  >
                    <MessageCircle className="w-3.5 h-3.5" />
                    Chat
                  </button>
                </div>
              </article>
            ))}
          </div>

          {/* Desktop / md+ — full table */}
          <div className="hidden md:block bg-white border border-zinc-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full table-fixed" data-testid="loads-table">
                <colgroup>
                  <col className="w-[6%]" />
                  <col className="w-[7%]" />
                  <col className="w-[23%]" />
                  <col className="w-[9%]" />
                  <col className="w-[10%]" />
                  <col className="w-[9%]" />
                  <col className="w-[6%]" />
                  <col className="w-[21%]" />
                  <col className="w-[9%]" />
                </colgroup>
                <thead className="bg-zinc-50 border-b border-zinc-200">
                  <tr>
                    <th className="px-3 py-3 text-left text-xs font-bold uppercase tracking-wider text-zinc-500">Ref #</th>
                    <th className="px-3 py-3 text-left text-xs font-bold uppercase tracking-wider text-zinc-500">Type</th>
                    <th className="px-3 py-3 text-left text-xs font-bold uppercase tracking-wider text-zinc-500">Route</th>
                    <th className="px-3 py-3 text-left text-xs font-bold uppercase tracking-wider text-zinc-500">Pickup</th>
                    <th className="px-3 py-3 text-left text-xs font-bold uppercase tracking-wider text-zinc-500">Equipment</th>
                    <th className="px-3 py-3 text-right text-xs font-bold uppercase tracking-wider text-zinc-500">Weight</th>
                    <th className="px-3 py-3 text-right text-xs font-bold uppercase tracking-wider text-zinc-500">Miles</th>
                    <th className="px-3 py-3 text-left text-xs font-bold uppercase tracking-wider text-zinc-500">Contact Dispatcher</th>
                    <th className="px-3 py-3 text-center text-xs font-bold uppercase tracking-wider text-zinc-500">Chat</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100">
                  {loads.map((load) => (
                    <tr key={load.id} className="table-row-hover align-top" data-testid={`load-row-${load.reference_no}`}>
                      <td className="px-3 py-4">
                        <span className="font-mono font-bold text-zinc-900 text-sm whitespace-nowrap">#{load.reference_no}</span>
                      </td>
                      <td className="px-3 py-4">
                        <span className="inline-flex items-center px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide bg-zinc-100 text-zinc-700 whitespace-nowrap">
                          {load.load_type}
                        </span>
                      </td>
                      <td className="px-3 py-4">
                        <div className="space-y-1 min-w-0">
                          <div className="flex items-start gap-1.5 min-w-0">
                            <MapPin className="w-3.5 h-3.5 text-green-600 flex-shrink-0 mt-0.5" />
                            <span className="text-sm font-medium text-zinc-900 truncate" title={load.pickup}>{load.pickup}</span>
                          </div>
                          <div className="flex items-start gap-1.5 min-w-0">
                            <MapPin className="w-3.5 h-3.5 text-red-600 flex-shrink-0 mt-0.5" />
                            <span className="text-sm text-zinc-700 truncate" title={load.destination}>{load.destination}</span>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-4">
                        <div className="flex items-center gap-1.5 whitespace-nowrap">
                          <Calendar className="w-3.5 h-3.5 text-zinc-400 flex-shrink-0" />
                          <span className="text-xs text-zinc-700">{formatDate(load.pickup_date)}</span>
                        </div>
                      </td>
                      <td className="px-3 py-4">
                        <div className="min-w-0">
                          <div className="flex items-center gap-1.5 min-w-0">
                            <Truck className="w-3.5 h-3.5 text-zinc-400 flex-shrink-0" />
                            <span className="text-xs text-zinc-700 truncate" title={load.truck_type}>{load.truck_type}</span>
                          </div>
                          {load.commodity && (
                            <div className="text-[10px] text-zinc-400 truncate mt-0.5 pl-5" title={load.commodity}>
                              {load.commodity}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-4 text-right">
                        <span className="font-mono text-xs text-zinc-700 whitespace-nowrap">
                          {load.weight ? `${Number(load.weight).toLocaleString()} lbs` : '—'}
                        </span>
                      </td>
                      <td className="px-3 py-4 text-right">
                        <span className="font-mono text-xs text-zinc-700 whitespace-nowrap">
                          {load.miles ? `${load.miles} mi` : '—'}
                        </span>
                      </td>
                      <td className="px-3 py-4">
                        <div className="space-y-1 min-w-0">
                          <a
                            href={`tel:${load.agent_phone}`}
                            className="flex items-center gap-1.5 text-sm text-red-600 hover:text-red-700 font-bold whitespace-nowrap"
                            data-testid={`call-agent-${load.reference_no}`}
                          >
                            <Phone className="w-3 h-3 flex-shrink-0" />
                            {load.agent_phone}
                          </a>
                          <a
                            href={`mailto:${load.agent_email}`}
                            className="flex items-center gap-1.5 text-xs text-zinc-600 hover:text-zinc-900 min-w-0"
                            title={load.agent_email}
                            data-testid={`email-agent-${load.reference_no}`}
                          >
                            <Mail className="w-3 h-3 flex-shrink-0" />
                            <span className="truncate">{load.agent_email}</span>
                          </a>
                        </div>
                      </td>
                      <td className="px-3 py-4 text-center">
                        <button
                          onClick={() => openChatWithLoad(load.reference_no)}
                          className="inline-flex items-center gap-1.5 bg-zinc-900 hover:bg-red-600 text-white text-[10px] font-bold uppercase tracking-wider px-2.5 py-2 transition-colors whitespace-nowrap"
                          data-testid={`chat-about-load-${load.reference_no}`}
                          title={`Chat about load #${load.reference_no}`}
                        >
                          <MessageCircle className="w-3 h-3" />
                          Chat
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  </section>
);
